/// <reference types="vite-plugin-svgr/client" />
import { ReactComponent as AccordionArrow } from '../icons/accordion-arrow.svg';
import { Controller, FormProvider, UseFormReturn, get } from 'react-hook-form';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { ReactComponent as CalculatorArrow } from '../icons/calculator-arrow.svg';
import { formatCurrency, safeSum, useZodForm } from '../../utils';
import { InputRow, MoneyInput } from '../forms';
import { OnboardingFinancesFormShape } from '../../utils/onboarding';

export const expandedIncomeFormSchema = z.object({
  wages: z.number().min(0),
  retirement: z.number().min(0),
  other: z.number().min(0),
});

export const ExpandedIncomeForm: React.FC<{
  methods: UseFormReturn<OnboardingFinancesFormShape>;
}> = ({ methods: topLevelMethods }) => {
  const [expanded, setExpanded] = useState(false);
  const methods = useZodForm({ schema: expandedIncomeFormSchema });

  const watched = methods.watch(['wages', 'retirement', 'other']);

  const annualIncome = topLevelMethods.watch('annualIncome');
  const calculatedSum = safeSum(watched);

  if (expanded) {
    return (
      <div className="border-hardwood-400 bg-hardwood-50 relative mt-[32px] w-full rounded-[8px] border-[1px] p-[24px]">
        <AccordionArrow
          className="text-hardwood-400 absolute right-[14px] top-[11px] h-[20px] w-[20px] cursor-pointer"
          onClick={() => setExpanded(false)}
        />
        <div className="typo-body text-[16px] font-bold">Annual income</div>

        <FormProvider {...methods}>
          <form className="w-full">
            <div className="flex gap-4">
              <div className="mt-4 flex-1">
                <Controller
                  control={methods.control}
                  name="wages"
                  render={({ field }) => {
                    return (
                      <InputRow label="Wages" className="mt-2">
                        <MoneyInput
                          autoFocus
                          rightIcon={
                            <div className="typo-body-small text-sidewalk-500">
                              /yr
                            </div>
                          }
                          {...field}
                        />
                      </InputRow>
                    );
                  }}
                />
                <div className="mt-4">
                  <Controller
                    control={methods.control}
                    name="retirement"
                    render={({ field }) => {
                      return (
                        <InputRow label="Retirement" className="mt-2">
                          <MoneyInput
                            rightIcon={
                              <div className="typo-body-small text-sidewalk-500">
                                /yr
                              </div>
                            }
                            {...field}
                          />
                        </InputRow>
                      );
                    }}
                  />
                </div>
              </div>
              <div className="mt-6 flex-1">
                <div className="typo-body-small mt-[27px]">
                  Enter your income using these common types.
                </div>
              </div>
            </div>

            <div className="mt-4 flex gap-4">
              <div className="flex-1">
                <Controller
                  control={methods.control}
                  name="other"
                  render={({ field }) => {
                    return (
                      <InputRow label="Other income" className="mt-2">
                        <MoneyInput
                          rightIcon={
                            <div className="typo-body-small text-sidewalk-500">
                              /yr
                            </div>
                          }
                          {...field}
                        />
                      </InputRow>
                    );
                  }}
                />
              </div>
              <div className="flex-1">
                <div className="typo-body-small mt-[18px]">
                  Other types include bonuses, commissions, overtime, rental,
                  and self-employment income.
                </div>
              </div>
            </div>

            <div className="mt-[24px] flex gap-4">
              <div className="flex-1">
                <div className="flex flex-col">
                  <div className="typo-body bg-hardwood-200 flex h-[40px] items-center justify-center rounded-tl-[8px] rounded-tr-[8px] text-center font-bold">
                    Total: {formatCurrency(calculatedSum)}
                    /yr
                  </div>
                  <div
                    className="typo-button bg-morton-600 hover:bg-morton-700 flex h-[40px] cursor-pointer items-center justify-center rounded-bl-[8px] rounded-br-[8px] text-center text-white"
                    onClick={() => {
                      topLevelMethods.setValue('annualIncome', calculatedSum);
                    }}
                  >
                    USE THIS TOTAL
                  </div>
                </div>

                <div className="flex justify-center overflow-hidden">
                  <CalculatorArrow className="mt-[-6px]" />
                </div>
              </div>
              <div className="flex-1">
                <div className="typo-body-small">
                  You should likely use this sum as your total annual income,
                  but you can enter a different number, if you prefer.
                </div>
              </div>
            </div>

            <div className="mt-4 flex gap-4">
              <div className="flex-1">
                <Controller
                  control={topLevelMethods.control}
                  name="annualIncome"
                  render={({ field }) => {
                    return (
                      <InputRow label="Your total monthly income">
                        <MoneyInput
                          {...field}
                          fieldErrorOverride={get(
                            topLevelMethods.formState.errors,
                            'annualIncome'
                          )}
                          rightIcon={
                            <div className="typo-body-small text-sidewalk-500">
                              /yr
                            </div>
                          }
                        />
                      </InputRow>
                    );
                  }}
                />
              </div>

              <div className="flex-1"></div>
            </div>
          </form>
        </FormProvider>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-[220px] flex-col items-start justify-center gap-4">
      <Controller
        control={topLevelMethods.control}
        name="annualIncome"
        render={({ field }) => {
          return (
            <InputRow label="Annual income">
              <MoneyInput
                autoFocus
                {...field}
                fieldErrorOverride={get(
                  topLevelMethods.formState.errors,
                  'annualIncome'
                )}
                rightIcon={
                  <div className="typo-body-small text-sidewalk-500">/yr</div>
                }
              />
            </InputRow>
          );
        }}
      />

      <div className="w-full">
        <div className="typo-body-small text-hardwood-700">
          Not sure what income to include?
        </div>
        <a
          onClick={() => setExpanded(true)}
          className="typo-body-small text-morton-600 flex cursor-pointer items-center font-bold hover:underline"
        >
          Calculate your total income.
          <AccordionArrow className="text-morton-600 h-[20px] w-[20px] rotate-180" />
        </a>
      </div>
    </div>
  );
};
